import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  LANDING_COLUMN_WIDTH2,
  LANDING_COLUMN_WIDTH2_MOBILE,
  LANDING_COLUMN_GAP2,
  splitArchiveIndexColumnsByHeight,
  type LandingArchiveCandidate,
  type LandingArchiveGroup,
} from '../../shared/util/landingArchiveIndex';
import { splitExhibitionVenueIndexTwoColumns } from '../../shared/util/exhibitionVenueIndex';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { DeviceType } from '../../shared/enum/EnumRepository';

export interface LandingArchiveIndexProps {
  groups: LandingArchiveGroup[];
  onCandidateActivate: (candidate: LandingArchiveCandidate) => void;
  onCandidatePreviewChange: (candidate: LandingArchiveCandidate | null) => void;
}

const LandingArchiveIndex: React.FC<LandingArchiveIndexProps> = ({
  groups,
  onCandidateActivate,
  onCandidatePreviewChange,
}) => {
  const { deviceType } = useDeviceStore();
  const isMobile = deviceType === DeviceType.MOBILE;
  const containerRef = useRef<HTMLDivElement>(null);
  const measurementRef = useRef<HTMLDivElement>(null);
  const [desktopColumns, setDesktopColumns] = useState<LandingArchiveGroup[][]>([groups]);
  const mobileColumns = useMemo(
    () => (isMobile ? splitExhibitionVenueIndexTwoColumns(groups) : []),
    [groups, isMobile],
  );

  useLayoutEffect(() => {
    if (isMobile) return;
    const container = containerRef.current;
    const measurement = measurementRef.current;
    if (!container || !measurement) return;

    let active = true;
    let frame = 0;
    const measure = () => {
      frame = 0;
      if (!active) return;
      const maxHeight = Math.floor(container.getBoundingClientRect().height);
      if (maxHeight <= 0) return;

      const measurements = Array.from(measurement.children).map((section, index) => {
        const children = Array.from(section.children);
        const hasLabel = Boolean(groups[index].label);
        return {
          height: section.getBoundingClientRect().height,
          labelHeight: hasLabel ? children[0].getBoundingClientRect().height : 0,
          paddingBottom: parseFloat(getComputedStyle(section).paddingBottom) || 0,
          candidateHeights: children.slice(hasLabel ? 1 : 0).map((child) =>
            child.getBoundingClientRect().height
          ),
        };
      });
      const columns = splitArchiveIndexColumnsByHeight(groups, measurements, maxHeight);
      setDesktopColumns((previous) => (
        previous.length === columns.length && previous.every((column, columnIndex) =>
          column.length === columns[columnIndex].length && column.every((group, groupIndex) => {
            const next = columns[columnIndex][groupIndex];
            return group.letter === next.letter && group.label === next.label &&
              group.candidates.length === next.candidates.length &&
              group.candidates.every((candidate, index) => candidate === next.candidates[index]);
          })
        ) ? previous : columns
      ));
    };
    const scheduleMeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    measure();
    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(container);
    observer.observe(measurement);
    window.addEventListener('resize', scheduleMeasure);
    window.visualViewport?.addEventListener('resize', scheduleMeasure);
    document.fonts?.addEventListener('loadingdone', scheduleMeasure);
    document.fonts?.ready.then(() => {
      if (active) scheduleMeasure();
    });

    return () => {
      active = false;
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
      window.visualViewport?.removeEventListener('resize', scheduleMeasure);
      document.fonts?.removeEventListener('loadingdone', scheduleMeasure);
    };
  }, [groups, isMobile]);

  const handleCandidateClick = useCallback(
    (e: React.MouseEvent, candidate: LandingArchiveCandidate) => {
      e.stopPropagation();
      onCandidateActivate(candidate);
    },
    [onCandidateActivate]
  );

  const renderCandidateItem = (
    candidate: LandingArchiveCandidate,
    key: string
  ) => (
    <CandidateItem
      key={key}
      $isMobile={isMobile}
      $isInteractive={Boolean(candidate.aui?.trim())}
      onClick={(e) => handleCandidateClick(e, candidate)}
      role={candidate.aui?.trim() ? 'button' : 'presentation'}
      tabIndex={candidate.aui?.trim() ? 0 : -1}
      onFocus={() => onCandidatePreviewChange(candidate)}
      onBlur={() => onCandidatePreviewChange(null)}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') {
          onCandidatePreviewChange(candidate);
        }
      }}
      onPointerLeave={() => onCandidatePreviewChange(null)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onCandidateActivate(candidate);
        }
      }}
    >
      <CandidateUsername $isMobile={isMobile}>{candidate.username}</CandidateUsername>
    </CandidateItem>
  );

  const renderGroup = (group: LandingArchiveGroup) => (
    <GroupSection key={group.letter}>
      {group.label ? <GroupLabel>{group.label}</GroupLabel> : null}
      {group.candidates.map((candidate, index) =>
        renderCandidateItem(candidate, `${candidate.handle}-${candidate.username}-${index}`)
      )}
    </GroupSection>
  );

  return (
    <CandidateContainer
      ref={containerRef}
      $isMobile={isMobile}
      data-mobile={isMobile ? 'true' : 'false'}
      data-archive-index-scroll=""
    >
      {!isMobile && (
        <MeasurementContainer aria-hidden="true">
          <Column ref={measurementRef} $isMobile={false}>
            {groups.map((group) => (
              <GroupSection key={group.letter}>
                {group.label ? <GroupLabel>{group.label}</GroupLabel> : null}
                {group.candidates.map((candidate, index) => (
                  <CandidateItem key={index} $isMobile={false} $isInteractive={false}>
                    <CandidateUsername $isMobile={false}>{candidate.username}</CandidateUsername>
                  </CandidateItem>
                ))}
              </GroupSection>
            ))}
          </Column>
        </MeasurementContainer>
      )}
      <ColumnList>
        {(isMobile ? mobileColumns : desktopColumns).map((column, columnIndex) => (
          <Column key={columnIndex} $isMobile={isMobile}>
            {column.map(renderGroup)}
          </Column>
        ))}
      </ColumnList>
    </CandidateContainer>
  );
};

const CandidateContainer = styled.div<{ $isMobile: boolean }>`
  position: relative;
  width: 100%;
  max-width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-sizing: border-box;
  overflow-x: ${({ $isMobile }) => ($isMobile ? 'hidden' : 'auto')};
  /* 한 이름 자체가 화면보다 큰 경우에도 스크롤로 접근할 수 있다. */
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ColumnList = styled.div`
  width: max-content;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  gap: ${LANDING_COLUMN_GAP2};
`;

const Column = styled.div<{ $isMobile: boolean }>`
  width: ${({ $isMobile }) => ($isMobile ? LANDING_COLUMN_WIDTH2_MOBILE : LANDING_COLUMN_WIDTH2)};
  flex-shrink: 0;
`;

/* 측정용 긴 목록이 실제 스크롤 영역을 늘리지 않도록 자른다. */
const MeasurementContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
`;

const GroupSection = styled.section`
  width: 100%;
  min-width: 0;
  padding-bottom: 8px;
`;

const GroupLabel = styled.div`
  width: 100%;
  ${({ theme }) => theme.typography.Footer_02};
  color: ${({ theme }) => theme.colors.color_Gray_04};
  line-height: 1;
`;

const CandidateItem = styled.div<{ $isMobile: boolean; $isInteractive: boolean }>`
  width: 100%;
  min-width: ${({ $isMobile }) => ($isMobile ? 0 : 'auto')};
  display: flex;
  align-items: center;
  cursor: ${({ $isInteractive }) => ($isInteractive ? 'pointer' : 'default')};

  &:hover > div {
    transform: scale(0.95);
  }
`;

const CandidateUsername = styled.div<{ $isMobile: boolean }>`
  padding: 3px 0;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  transform-origin: left center;
  transition: transform 0.25s ease-in-out;
  ${({ $isMobile }) =>
    $isMobile
      ? `
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `
      : `
    width: clamp(100px, 50%, 180px);
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export default LandingArchiveIndex;
